/**
 * 3-DOF Manhattan-world refinement via Levenberg–Marquardt with
 * per-iteration line-to-axis reclassification (generalized EM).
 *
 * Jointly refines (pitch, yaw, focalLengthPx) AND the per-line axis
 * assignment against the perpendicular pixel-distance residuals of all
 * input line segments, under a Tukey biweight loss. The no-roll constraint
 * collapses the standard 4-DOF Manhattan refinement to 3 parameters:
 * rotation lives entirely in the yaw-pitch plane and world-Z always
 * projects to image-vertical, so the z-axis vanishing point is at column
 * `cx` for free — no extra penalty term.
 *
 * Each outer iteration:
 *   1. For every line without a hard axis label, recompute residuals to
 *      all three world axes at the current pose; assign the line to the
 *      axis with the smallest |residual| (hard argmin).
 *   2. Run one LM damping step to update (pitch, yaw, focal), holding
 *      that classification fixed within the step.
 *
 * This replaces the old pipeline's early hard commit to an axis label from
 * horizon-RANSAC clustering. A line mis-bucketed at RANSAC time (short
 * edge with noisy Hough slope, for example) is automatically corrected
 * once the LM moves the VPs to positions where the other axis fits better.
 *
 * The same kernel is used by:
 *
 *   - the auto-align worker, which seeds it from the horizon RANSAC's
 *     `(yHorizon, vpXLeft, vpXRight)` and lets the LM auto-classify every
 *     detected segment,
 *
 *   - cameraSolver.ts, which seeds it from user-drawn lines with fixed
 *     axis labels (user intent is not up for negotiation).
 *
 * Why LM and not Gauss-Newton: with rough VP initial guesses (pre-RANSAC),
 * GN can step into a regime where the perpendicular-distance Jacobian
 * vanishes and diverges. The damping term in LM keeps steps bounded.
 *
 * Why numeric Jacobian: 3 parameters × ~500 residuals × 2 perturbations =
 * ~3000 residual evaluations per LM iteration, which is microseconds, and
 * an analytic Jacobian here would be brittle and 200 LOC by itself.
 */

import {
  mat3MulVec3,
  rotationFromPitchYaw,
  type Vec2,
  type Vec3,
} from "./vpMath.ts";

export type Axis = "x" | "y" | "z";

export interface RefineLineInput {
  /** Normalized homogeneous line ℓ = (a, b, c) with a² + b² = 1, in pixel coords. */
  line: Vec3;
  /** Length-based weight; longer lines count more. */
  weight: number;
  /**
   * Optional hard axis label. When set, this line is constrained to the
   * given axis throughout refinement — use for user-drawn lines where the
   * axis is known by construction. When omitted, the LM reclassifies the
   * line at each outer iteration by taking the argmin over residuals to
   * the three world axes.
   */
  axis?: Axis;
}

export interface RefineInitial {
  pitch: number;
  yaw: number;
  focalPx: number;
}

export interface RefineInput {
  lines: RefineLineInput[];
  imgWidth: number;
  imgHeight: number;
  initial: RefineInitial;
  /** Called after each accepted LM step. Zero overhead when omitted. */
  onStep?: (snap: IterationSnapshot) => void;
}

export interface RefineResult {
  pitch: number;
  yaw: number;
  focalPx: number;
  /** Image-pixel position of the world-X vanishing point, or null if at infinity. */
  vpX: Vec2 | null;
  /** Image-pixel position of the world-Y vanishing point, or null if at infinity. */
  vpY: Vec2 | null;
  /**
   * Always null under the no-roll parameterization — world-Z is at column cx
   * and y = ±∞. Encoded explicitly so callers don't have to know that.
   */
  vpZ: null;
  /**
   * Final per-line axis assignment, parallel to the input `lines` array.
   * For hard-labeled inputs this equals the input axis; for auto-classified
   * inputs it's the argmin-residual axis at the converged pose. Callers use
   * this to color output lines without re-running classification themselves.
   */
  lineAxes: Axis[];
  /**
   * Perpendicular pixel residual of each line to its assigned axis's VP
   * (or angular residual for VPs near infinity, matching the LM's cost
   * function). Parallel to `lineAxes`. Callers filter weak classifications
   * by thresholding this array.
   */
  lineResiduals: number[];
  cost: number;
  iterations: number;
  converged: boolean;
}

/** Emitted after each accepted LM step when `RefineInput.onStep` is provided. */
export interface IterationSnapshot {
  iter: number;
  pitch: number;
  yaw: number;
  focalPx: number;
  cost: number;
  lambda: number;
  tukeyK: number;
  vpX: Vec2 | null;
  vpY: Vec2 | null;
}

/**
 * Tukey biweight cutoff schedule, in pixels. Residuals beyond `k` are
 * completely ignored — both weight and cost contribution drop to zero —
 * which is what makes the refinement robust to mislabeled or grossly noisy
 * lines. Huber is not enough here: at large residuals Huber's cost grows
 * linearly with distance, so a single mislabeled segment thousands of
 * pixels from its (wrongly-assumed) VP can dominate the total cost and
 * bias the solution.
 *
 * Graduated non-convexity: start with a wide cutoff so even rough initial
 * guesses have nonzero gradient from the bulk of inliers, then anneal down
 * to a tight cutoff as iterations progress. Inliers stay inside the basin
 * the whole time; outliers exit (weight → 0) once their residual exceeds
 * the shrinking k.
 */
const TUKEY_K_INITIAL = 400.0;
const TUKEY_K_FINAL = 6.0;
const TUKEY_ANNEAL_ITERATIONS = 18;
const MAX_ITERATIONS = 60;
const STEP_TOLERANCE = 1e-6;
const COST_TOLERANCE = 1e-9;
/**
 * Focal length bounds in working-image pixels. Corresponds roughly to
 * 7–200 mm on a 36 mm sensor at 1600 px long edge (f_px ≈ f_mm × 44).
 * Prevents the LM from drifting into degenerate near-zero or near-orthographic
 * focal regimes when the evidence is weak.
 */
const MIN_FOCAL_PX = 100;
const MAX_FOCAL_PX = 12000;

/** k(iter) — geometric anneal from K_INITIAL to K_FINAL over the first ANNEAL iters. */
function tukeyKAt(iter: number): number {
  if (iter >= TUKEY_ANNEAL_ITERATIONS) return TUKEY_K_FINAL;
  const t = iter / TUKEY_ANNEAL_ITERATIONS;
  const logK =
    Math.log(TUKEY_K_INITIAL) * (1 - t) + Math.log(TUKEY_K_FINAL) * t;
  return Math.exp(logK);
}

/**
 * Project the world axis `e_a` to its image-pixel vanishing point under
 * the given (pitch, yaw, focal). Returns `null` for VPs at infinity
 * (which happens iff `r.z ≈ 0`, i.e., the world axis lies in the camera's
 * image plane).
 *
 * Standard pinhole projection with the camera looking along -Z:
 *
 *   image_x = cx − f · X / Z      (right of cx when X > 0, Z < 0)
 *   image_y = cy + f · Y / Z      (above cy when Y > 0, Z < 0 — image y is down)
 *
 * Equivalent to using "depth" `−Z`: image_x = cx + f·X/(−Z), etc.
 */
export function projectAxisVP(
  axis: Vec3,
  pitch: number,
  yaw: number,
  focalPx: number,
  cx: number,
  cy: number,
): Vec2 | null {
  const R = rotationFromPitchYaw(pitch, yaw);
  const r = mat3MulVec3(R, axis);
  if (Math.abs(r[2]) < 1e-9) return null;
  return [cx - (focalPx * r[0]) / r[2], cy + (focalPx * r[1]) / r[2]];
}

/**
 * Residual of a single line against a specific world axis under the current
 * (pitch, yaw, focal). For a finite VP this is the signed perpendicular
 * pixel distance from the VP to the line. For a VP at infinity
 * (parallel-line case) it's the angular alignment residual: dot of the line
 * normal with the world-axis direction in camera frame.
 *
 * The axis is passed explicitly rather than read off the line so the same
 * function can serve both the hard-labeled path and the auto-classify path,
 * which needs to evaluate every line against all three axes per iteration.
 */
function residualForAxis(
  line: Vec3,
  axis: Axis,
  pitch: number,
  yaw: number,
  focalPx: number,
  cx: number,
  cy: number,
): number {
  const e: Vec3 =
    axis === "x" ? [1, 0, 0] : axis === "y" ? [0, 1, 0] : [0, 0, 1];

  const R = rotationFromPitchYaw(pitch, yaw);
  const r = mat3MulVec3(R, e);

  if (Math.abs(r[2]) < 0.05) {
    // VP at infinity (or far enough that the finite formula gives residuals
    // of f/0.05 ≈ 20·f pixels — far outside the Tukey window). Use the
    // angular alignment residual instead: dot of the line normal with the
    // world-axis image-plane direction. This keeps z-line residuals
    // well-conditioned at low pitch angles where the z-VP is effectively
    // at infinity (common for level-camera street and architectural shots).
    return -line[0] * r[0] + line[1] * r[1];
  }

  const vpX = cx - (focalPx * r[0]) / r[2];
  const vpY = cy + (focalPx * r[1]) / r[2];
  return line[0] * vpX + line[1] * vpY + line[2];
}

/**
 * Pick the best world axis for a line at the current pose. Hard-labeled
 * lines return their fixed axis; auto-classify lines return the argmin over
 * the three axes by |residual|. Called at the start of every outer LM
 * iteration — this is the E-step of the generalized EM loop.
 */
function classifyLine(
  l: RefineLineInput,
  pitch: number,
  yaw: number,
  focalPx: number,
  cx: number,
  cy: number,
): Axis {
  if (l.axis != null) return l.axis;
  const rx = Math.abs(
    residualForAxis(l.line, "x", pitch, yaw, focalPx, cx, cy),
  );
  const ry = Math.abs(
    residualForAxis(l.line, "y", pitch, yaw, focalPx, cx, cy),
  );
  const rz = Math.abs(
    residualForAxis(l.line, "z", pitch, yaw, focalPx, cx, cy),
  );
  if (rx <= ry && rx <= rz) return "x";
  if (ry <= rz) return "y";
  return "z";
}

/**
 * Tukey biweight cost: smooth M-estimator that asymptotes to a constant
 * (k²/6) for outliers, so residuals beyond `k` pixels contribute nothing
 * to the gradient and the same fixed value to the cost. This is the
 * essential property that lets refineManhattan converge correctly even
 * when a few lines are mislabeled or grossly mispositioned.
 *
 *   ρ(r) = (k²/6) · [1 − (1 − (r/k)²)³]   for |r| ≤ k
 *   ρ(r) = k²/6                            for |r| > k
 */
function tukeyCost(r: number, k: number): number {
  const ar = Math.abs(r);
  if (ar >= k) return (k * k) / 6;
  const t = 1 - (r / k) * (r / k);
  return ((k * k) / 6) * (1 - t * t * t);
}

/**
 * Tukey IRLS weight: w(r) = (1 − (r/k)²)² for |r| ≤ k, else 0.
 * Used in the linear-system construction.
 */
function tukeyWeight(r: number, k: number): number {
  const ar = Math.abs(r);
  if (ar >= k) return 0;
  const t = 1 - (r / k) * (r / k);
  return t * t;
}

/**
 * Total weighted Tukey cost over all lines, at the given k and fixed axis
 * assignment. Cost monotonicity within an LM damping step is only valid
 * when the axis array doesn't change, so callers must pass the same axes
 * they want compared across old/new pose candidates.
 */
function totalCost(
  lines: RefineLineInput[],
  axes: Axis[],
  pitch: number,
  yaw: number,
  focalPx: number,
  cx: number,
  cy: number,
  k: number,
): number {
  let cost = 0;
  for (let i = 0; i < lines.length; i++) {
    const r = residualForAxis(
      lines[i].line,
      axes[i],
      pitch,
      yaw,
      focalPx,
      cx,
      cy,
    );
    cost += lines[i].weight * tukeyCost(r, k);
  }
  return cost;
}

/**
 * Closed-form solver for a 3×3 symmetric positive-definite linear system
 * via Cholesky factorization. Used for the Levenberg–Marquardt normal
 * equations `(JᵀWJ + λI) Δp = -JᵀWr`. Returns null on numerical failure
 * so the LM loop can fall back to a larger λ.
 */
function solve3x3SPD(
  m: [number, number, number, number, number, number, number, number, number],
  b: [number, number, number],
): [number, number, number] | null {
  const a11 = m[0];
  const a12 = m[1];
  const a13 = m[2];
  const a22 = m[4];
  const a23 = m[5];
  const a33 = m[8];

  if (a11 <= 0) return null;
  const l11 = Math.sqrt(a11);
  const l21 = a12 / l11;
  const l31 = a13 / l11;

  const d22 = a22 - l21 * l21;
  if (d22 <= 0) return null;
  const l22 = Math.sqrt(d22);
  const l32 = (a23 - l31 * l21) / l22;

  const d33 = a33 - l31 * l31 - l32 * l32;
  if (d33 <= 0) return null;
  const l33 = Math.sqrt(d33);

  // Forward substitution L y = b.
  const y1 = b[0] / l11;
  const y2 = (b[1] - l21 * y1) / l22;
  const y3 = (b[2] - l31 * y1 - l32 * y2) / l33;

  // Backward substitution Lᵀ x = y.
  const x3 = y3 / l33;
  const x2 = (y2 - l32 * x3) / l22;
  const x1 = (y1 - l21 * x2 - l31 * x3) / l11;

  return [x1, x2, x3];
}

/**
 * Run Levenberg–Marquardt with per-iteration axis reclassification.
 *
 * Each outer iteration:
 *   1. E-step: classify every line (argmin residual over the 3 axes, or
 *      the fixed hard label if `l.axis` is set).
 *   2. M-step: one LM damping step on (pitch, yaw, focalPx), with the
 *      classification held fixed so cost comparisons within the damping
 *      loop are consistent.
 *
 * Reclassification is cost-monotone by construction — taking the argmin
 * can only lower (or tie) `Σ tukeyCost(r, k)` for each line — so the
 * combined (classify → LM) sequence monotonically decreases the total
 * cost, and the usual LM convergence checks still apply.
 *
 * The Jacobian is numeric central differences with parameter-aware step:
 *
 *   Δpitch = Δyaw = 1e-5 rad   (small enough to be in the linear regime)
 *   Δf     = max(1e-3 · f, 1)  (focal varies over a much wider scale)
 */
export function refineManhattan(input: RefineInput): RefineResult {
  const { lines, imgWidth, imgHeight, initial } = input;
  const cx = imgWidth / 2;
  const cy = imgHeight / 2;

  let pitch = initial.pitch;
  let yaw = initial.yaw;
  let focalPx = Math.max(1, initial.focalPx);

  let lambda = 1e-3;
  let kCurrent = tukeyKAt(0);
  let axes: Axis[] = lines.map((l) =>
    classifyLine(l, pitch, yaw, focalPx, cx, cy),
  );
  let cost = totalCost(lines, axes, pitch, yaw, focalPx, cx, cy, kCurrent);
  let iterations = 0;
  let converged = false;

  if (lines.length < 3) {
    // Too few lines to refine — just package the initial guess.
    const residualsShort = lines.map((l, i) =>
      Math.abs(residualForAxis(l.line, axes[i], pitch, yaw, focalPx, cx, cy)),
    );
    return packageResult(
      pitch,
      yaw,
      focalPx,
      cx,
      cy,
      cost,
      iterations,
      false,
      axes,
      residualsShort,
    );
  }

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    iterations = iter + 1;
    const costBefore = cost;

    // Anneal the Tukey cutoff. Recompute the current cost at the new k so
    // accept/reject decisions are consistent within this iteration.
    const newK = tukeyKAt(iter);
    if (newK !== kCurrent) {
      kCurrent = newK;
      cost = totalCost(lines, axes, pitch, yaw, focalPx, cx, cy, kCurrent);
    }

    // E-step: reclassify auto-lines at the current pose. Argmin is
    // cost-monotone per line, so `cost` can only drop here.
    axes = lines.map((l) => classifyLine(l, pitch, yaw, focalPx, cx, cy));
    cost = totalCost(lines, axes, pitch, yaw, focalPx, cx, cy, kCurrent);

    // M-step: build the normal equations JᵀWJ Δp = -JᵀWr via central
    // differences, holding `axes` fixed so the Jacobian is consistent.
    const fStep = Math.max(1e-3 * focalPx, 1);
    const aStep = 1e-5;

    let H00 = 0,
      H01 = 0,
      H02 = 0;
    let H11 = 0,
      H12 = 0;
    let H22 = 0;
    let g0 = 0,
      g1 = 0,
      g2 = 0;

    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const ax = axes[i];
      const r0 = residualForAxis(l.line, ax, pitch, yaw, focalPx, cx, cy);
      const w = l.weight * tukeyWeight(r0, kCurrent);
      if (w === 0) continue;

      const rPitchPlus = residualForAxis(
        l.line,
        ax,
        pitch + aStep,
        yaw,
        focalPx,
        cx,
        cy,
      );
      const rPitchMinus = residualForAxis(
        l.line,
        ax,
        pitch - aStep,
        yaw,
        focalPx,
        cx,
        cy,
      );
      const dRdPitch = (rPitchPlus - rPitchMinus) / (2 * aStep);

      const rYawPlus = residualForAxis(
        l.line,
        ax,
        pitch,
        yaw + aStep,
        focalPx,
        cx,
        cy,
      );
      const rYawMinus = residualForAxis(
        l.line,
        ax,
        pitch,
        yaw - aStep,
        focalPx,
        cx,
        cy,
      );
      const dRdYaw = (rYawPlus - rYawMinus) / (2 * aStep);

      const rFPlus = residualForAxis(
        l.line,
        ax,
        pitch,
        yaw,
        focalPx + fStep,
        cx,
        cy,
      );
      const rFMinus = residualForAxis(
        l.line,
        ax,
        pitch,
        yaw,
        focalPx - fStep,
        cx,
        cy,
      );
      const dRdF = (rFPlus - rFMinus) / (2 * fStep);

      H00 += w * dRdPitch * dRdPitch;
      H01 += w * dRdPitch * dRdYaw;
      H02 += w * dRdPitch * dRdF;
      H11 += w * dRdYaw * dRdYaw;
      H12 += w * dRdYaw * dRdF;
      H22 += w * dRdF * dRdF;
      g0 += w * dRdPitch * r0;
      g1 += w * dRdYaw * r0;
      g2 += w * dRdF * r0;
    }

    // Apply LM damping λ to the diagonal.
    let acceptedStep = false;
    for (let lmTry = 0; lmTry < 10; lmTry++) {
      const M: [
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
      ] = [
        H00 + lambda * (1 + H00),
        H01,
        H02,
        H01,
        H11 + lambda * (1 + H11),
        H12,
        H02,
        H12,
        H22 + lambda * (1 + H22),
      ];
      const rhs: [number, number, number] = [-g0, -g1, -g2];
      const dp = solve3x3SPD(M, rhs);
      if (!dp) {
        lambda *= 4;
        continue;
      }

      const newPitch = pitch + dp[0];
      const newYaw = yaw + dp[1];
      const newFocal = Math.max(
        MIN_FOCAL_PX,
        Math.min(MAX_FOCAL_PX, focalPx + dp[2]),
      );
      // Cost comparison uses the SAME axes — that's what makes the LM step
      // a proper descent step. Reclassification happens only at the top of
      // the next outer iteration.
      const newCost = totalCost(
        lines,
        axes,
        newPitch,
        newYaw,
        newFocal,
        cx,
        cy,
        kCurrent,
      );

      if (newCost < cost) {
        const stepNorm = Math.hypot(dp[0], dp[1], dp[2] / Math.max(1, focalPx));
        pitch = newPitch;
        yaw = newYaw;
        focalPx = newFocal;
        cost = newCost;
        lambda = Math.max(1e-9, lambda / 3);
        acceptedStep = true;
        input.onStep?.({
          iter,
          pitch,
          yaw,
          focalPx,
          cost,
          lambda,
          tukeyK: kCurrent,
          vpX: projectAxisVP([1, 0, 0], pitch, yaw, focalPx, cx, cy),
          vpY: projectAxisVP([0, 1, 0], pitch, yaw, focalPx, cx, cy),
        });
        if (stepNorm < STEP_TOLERANCE) {
          converged = true;
        }
        break;
      } else {
        lambda *= 4;
      }
    }

    if (!acceptedStep) break;
    if (converged) break;
    // Compare the full outer-iteration cost drop (reclassification + LM).
    if (Math.abs(costBefore - cost) < COST_TOLERANCE) {
      converged = true;
      break;
    }
  }

  // One final classify so the returned `lineAxes` reflects the converged pose.
  axes = lines.map((l) => classifyLine(l, pitch, yaw, focalPx, cx, cy));
  const residuals = lines.map((l, i) =>
    Math.abs(residualForAxis(l.line, axes[i], pitch, yaw, focalPx, cx, cy)),
  );
  return packageResult(
    pitch,
    yaw,
    focalPx,
    cx,
    cy,
    cost,
    iterations,
    converged,
    axes,
    residuals,
  );
}

function packageResult(
  pitch: number,
  yaw: number,
  focalPx: number,
  cx: number,
  cy: number,
  cost: number,
  iterations: number,
  converged: boolean,
  lineAxes: Axis[],
  lineResiduals: number[],
): RefineResult {
  const vpX = projectAxisVP([1, 0, 0], pitch, yaw, focalPx, cx, cy);
  const vpY = projectAxisVP([0, 1, 0], pitch, yaw, focalPx, cx, cy);
  return {
    pitch,
    yaw,
    focalPx,
    vpX,
    vpY,
    vpZ: null,
    lineAxes,
    lineResiduals,
    cost,
    iterations,
    converged,
  };
}

/**
 * Build an initial (pitch, yaw, focalPx) guess from a horizon RANSAC fit.
 *
 * Under the no-roll constraint, with the principal point at the image
 * center and the two horizon vanishing points straddling cx, the standard
 * 2-point orthogonality formula simplifies to
 *
 *   f² = −(vpXLeft − cx)·(vpXRight − cx) − h²
 *
 * where `h = yHorizon − cy` is the perpendicular pixel distance from the
 * principal point to the horizon. (The classical form is `f² = d₁d₂ − h²`
 * with the horizon row removed via `dL < 0 < dR`, which makes `−dL·dR`
 * positive.) When pitch is zero the horizon passes through the principal
 * point and `h = 0`, recovering `f² = −dL·dR`. With non-zero pitch the
 * `h²` correction is essential — without it the focal length is biased
 * upward by tens of pixels at typical pitch angles.
 *
 * Once focal is known:
 *
 *   pitch = atan2(cy − yHorizon, focalPx)            (horizon above center → camera looking down)
 *
 *   tan(yaw) = focalPx / ((vpXRight − cx) · cos(pitch))
 *   ⇒ yaw   = atan2(focalPx, (vpXRight − cx) · cos(pitch))
 *
 * The yaw formula assumes the right horizon VP is world-X. If the true X
 * axis is on the left, the initial yaw is ~π/2 off and the LM may converge
 * to a local minimum where X and Y are swapped. Callers should run the LM
 * from both VP assignments (swapping vpXLeft↔vpXRight) and keep the lower-cost
 * result — see autoAlignWorker.ts for the canonical pattern.
 */
export function initialGuessFromHorizon(
  yHorizon: number,
  vpXLeft: number,
  vpXRight: number,
  imgWidth: number,
  imgHeight: number,
): RefineInitial | null {
  const cx = imgWidth / 2;
  const cy = imgHeight / 2;

  const dL = vpXLeft - cx;
  const dR = vpXRight - cx;
  const h = yHorizon - cy;
  const fSqr = -dL * dR - h * h;
  if (fSqr <= 0) return null;
  const focalPx = Math.sqrt(fSqr);

  const pitch = Math.atan2(-h, focalPx);
  const cp = Math.cos(pitch);
  const yaw = Math.atan2(focalPx, dR * cp);

  return { pitch, yaw, focalPx };
}
